/**
 * Parts Inventory API Client
 */

import { get, post, put, del } from '@/lib/api-client';

// Types
export interface GroupCustomField {
  id: string;
  groupId: string;
  key: string;
  name: string;
  nameEn?: string;
  type: string;
  required: boolean;
  visible: boolean;
  editable: boolean;
  searchable: boolean;
  options?: string[];
  validation?: Record<string, any>;
  description?: string;
  placeholder?: string;
  helpText?: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PartGroup {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  partsCount: number;
  fieldsCount: number;
  customFields?: GroupCustomField[];
  createdAt: string;
  updatedAt: string;
}

export type PartStatus = 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED' | 'OBSOLETE';
export type StockStatus = 'NORMAL' | 'LOW' | 'OUT_OF_STOCK';

export interface Part {
  id: string;
  partNumber: string;
  partNameEn: string;  // 英文名称（必填）
  partNameCn?: string;  // 中文名称（可选）
  description?: string;
  category?: string;
  specifications?: string;
  station?: string;         // 旧字段，兼容
  warehouseLocation?: string; // 旧字段，兼容
  stationId?: string;       // 新字段：关联工位ID
  warehouseId?: string;     // 新字段：关联仓库ID
  storageLocationId?: string; // 新字段：关联仓位ID
  warehouse?: {
    id: string;
    code: string;
    nameEn: string;
    nameCn?: string;
  };
  storageLocation?: {
    id: string;
    code: string;
    nameEn: string;
    nameCn?: string;
    zone?: string;
  };
  currentStock: number;
  minStock: number;
  maxStock?: number;
  unit: string;
  supplier?: string;
  supplierPartNumber?: string;
  leadTime?: number;
  unitPrice?: number;
  currency: string;
  imageUrl?: string;
  status: PartStatus;
  stockStatus: StockStatus; // 库存状态（计算字段）
  metadata?: any;
  customFields?: Record<string, any>;
  groups?: Array<{
    group: PartGroup;
    partId: string;
    groupId: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  deletedAt?: string;
}

export interface PartLabel {
  id: string;
  partId: string;
  labelCode: string;
  qrCodeData: string;
  template: string;
  size: string;
  printCount: number;
  lastPrintedAt?: string;
  printedBy?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'VOID';
  createdAt: string;
  updatedAt: string;
}

export interface InventoryLog {
  id: string;
  partId: string;
  operationType: string;
  quantity: number;
  previousStock: number;
  newStock: number;
  station?: string;
  warehouseLocation?: string;
  reason?: string;
  operatorId: string;
  operatorName: string;
  scannedCode?: string;
  createdAt: string;
}

export interface StockAlert {
  id: string;
  partId: string;
  alertType: string;
  severity: string;
  currentStock: number;
  threshold: number;
  message: string;
  status: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  resolution?: string;
  createdAt: string;
  updatedAt: string;
}

// 分页响应类型
export interface PartsListResponse {
  items: Part[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Parts API
export const partsApi = {
  // List parts
  list: (params?: {
    search?: string;
    partNumber?: string;
    partNameCn?: string;
    partNameEn?: string;
    category?: string;
    station?: string;
    warehouseLocation?: string;
    status?: PartStatus;
    stockStatus?: StockStatus;
    lowStock?: boolean;
    outOfStock?: boolean;
    groupIds?: string[];
    page?: number;
    limit?: number;
  }): Promise<PartsListResponse> => get('/parts', { params }),

  // Get part by identifier (UUID/partNumber)
  get: (identifier: string): Promise<Part> => get(`/parts/${identifier}`),

  // @deprecated 使用 get(identifier) 替代
  getByPartNumber: (partNumber: string): Promise<Part> => get(`/parts/${partNumber}`),

  // @deprecated 使用 get(identifier) 替代
  getByCode: (code: string): Promise<Part> => get(`/parts/${code}`),

  // Create part
  create: (data: Partial<Part>): Promise<Part> => post('/parts', data),

  // Update part by identifier (UUID/partNumber)
  update: (identifier: string, data: Partial<Part>): Promise<Part> => put(`/parts/${identifier}`, data),

  // Delete part by identifier (UUID/partNumber)
  delete: (identifier: string): Promise<void> => del(`/parts/${identifier}`),

  // Bulk import
  bulkImport: (data: { parts: Partial<Part>[]; updateExisting?: boolean; skipErrors?: boolean }): Promise<{ total: number; success: number; failed: number; errors: any[] }> =>
    post('/parts/bulk-import', data),

  // Get inventory stats
  getStats: (params?: { station?: string; warehouseLocation?: string; category?: string }): Promise<any> =>
    get('/parts/stats/inventory', { params }),

  // Get metadata
  getCategories: (): Promise<string[]> => get('/parts/meta/categories'),
  getStations: (): Promise<string[]> => get('/parts/meta/stations'),
  getWarehouseLocations: (): Promise<string[]> => get('/parts/meta/warehouse-locations'),
};

// Inventory API
export const inventoryApi = {
  // Check-in
  checkIn: (data: {
    code: string;
    quantity: number;
    operationType?: string;
    station?: string;
    warehouseLocation?: string;
    reason?: string;
  }): Promise<{ part: Part; log: InventoryLog; message: string }> => post('/inventory/check-in', data),

  // Check-out
  checkOut: (data: {
    code: string;
    quantity: number;
    station: string;
    warehouseLocation?: string;
    reason?: string;
  }): Promise<{ part: Part; log: InventoryLog; message: string }> => post('/inventory/check-out', data),

  // Adjust inventory
  adjust: (data: {
    partId: string;
    newStock: number;
    reason: string;
    station?: string;
    warehouseLocation?: string;
  }): Promise<{ part: Part; log: InventoryLog; message: string }> => post('/inventory/adjust', data),

  // Transfer inventory
  transfer: (data: {
    partId: string;
    quantity: number;
    fromStation: string;
    toStation: string;
    fromWarehouseLocation?: string;
    toWarehouseLocation?: string;
    reason?: string;
  }): Promise<{ log: InventoryLog; message: string }> => post('/inventory/transfer', data),

  // Get logs
  getLogs: (params?: {
    partId?: string;
    partNumber?: string;
    operationType?: string;
    station?: string;
    warehouseLocation?: string;
    operatorId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: InventoryLog[]; total: number }> => get('/inventory/logs', { params }),

  // Get part history
  getPartHistory: (partId: string, days?: number): Promise<InventoryLog[]> =>
    get(`/inventory/history/${partId}`, { params: { days } }),

  // Get stats
  getStats: (params?: {
    station?: string;
    warehouseLocation?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    operationType?: string;
  }): Promise<any> => get('/inventory/stats', { params }),

  // Bulk operation
  bulkOperation: (data: { operations: any[]; skipErrors?: boolean }): Promise<{ total: number; success: number; failed: number; errors: any[] }> =>
    post('/inventory/bulk-operation', data),
};

// Labels API
export const labelsApi = {
  // Generate label
  generate: (data: { partId: string; template?: string; size?: string; quantity?: number }): Promise<{ labels: PartLabel[]; message: string }> =>
    post('/labels/generate', data),

  // Bulk generate
  bulkGenerate: (data: { partIds: string[]; template?: string; size?: string; quantityPerPart?: number }): Promise<{ total: number; success: number; failed: number; labels: PartLabel[]; errors: any[] }> =>
    post('/labels/generate/bulk', data),

  // Print label
  print: (data: { labelId: string; printerName?: string }): Promise<{ label: PartLabel; printData: any; message: string }> =>
    post('/labels/print', data),

  // Bulk print
  bulkPrint: (data: { labelIds: string[]; printerName?: string }): Promise<{ total: number; success: number; failed: number; printData: any[]; errors: any[] }> =>
    post('/labels/print/bulk', data),

  // Get label
  get: (id: string): Promise<PartLabel> => get(`/labels/${id}`),

  // Get by label code
  getByCode: (labelCode: string): Promise<PartLabel> => get(`/labels/by-code/${labelCode}`),

  // Get by part ID
  getByPartId: (partId: string): Promise<PartLabel[]> => get(`/labels/by-part/${partId}`),

  // Void label
  void: (id: string): Promise<void> => del(`/labels/${id}`),

  // Get print stats
  getPrintStats: (params?: { startDate?: string; endDate?: string }): Promise<any> =>
    get('/labels/stats/print', { params }),
};

// Alerts API
export const alertsApi = {
  // List alerts
  list: (params?: {
    partId?: string;
    alertType?: string;
    severity?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: StockAlert[]; total: number }> => get('/alerts', { params }),

  // Get alert
  get: (id: string): Promise<StockAlert> => get(`/alerts/${id}`),

  // Acknowledge alert
  acknowledge: (data: { alertId: string; note?: string }): Promise<{ alert: StockAlert; message: string }> =>
    post('/alerts/acknowledge', data),

  // Resolve alert
  resolve: (data: { alertId: string; resolution: string; newStock?: number }): Promise<{ alert: StockAlert; message: string }> =>
    post('/alerts/resolve', data),

  // Bulk acknowledge
  bulkAcknowledge: (alertIds: string[]): Promise<{ updated: number; message: string }> =>
    post('/alerts/bulk-acknowledge', { alertIds }),

  // Bulk resolve
  bulkResolve: (alertIds: string[], resolution: string): Promise<{ updated: number; message: string }> =>
    post('/alerts/bulk-resolve', { alertIds, resolution }),

  // Get stats
  getStats: (): Promise<any> => get('/alerts/stats/summary'),

  // Auto resolve
  autoResolve: (): Promise<{ resolved: number; message: string }> => post('/alerts/auto-resolve'),
};

// Excel API
export const excelApi = {
  // Import Excel
  import: (data: { rows: any[]; fileName: string; fileSize: number; updateExisting?: boolean; groupIds?: string[] }): Promise<any> =>
    post('/parts/excel/import', data),

  // Export Excel
  export: (params?: { category?: string; station?: string; warehouseLocation?: string; status?: string }): Promise<any[]> =>
    get('/parts/excel/export', { params }),

  // Get template
  getTemplate: (): Promise<any[]> => get('/parts/excel/template'),

  // Validate data
  validate: (rows: any[]): Promise<{ valid: boolean; errors: any[] }> => post('/parts/excel/validate', { rows }),

  // Get import history
  getHistory: (params?: { page?: number; limit?: number }): Promise<any> =>
    get('/parts/excel/import-history', { params }),

  // Get import log
  getLog: (id: string): Promise<any> => get(`/parts/excel/import-log/${id}`),
};

// ===================================
// Column Configuration API
// ===================================

export interface ColumnDefinition {
  key: string;
  label: string;
  type: 'standard' | 'custom';
  visible: boolean;
  width?: number;
  fixed?: 'left' | 'right' | 'none';
  sortable?: boolean;
  searchable?: boolean;
  order: number;
  format?: string;
}

export interface ColumnConfig {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isTemplate: boolean;
  columns: ColumnDefinition[];
  isPublic: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateColumnConfigDto {
  name: string;
  description?: string;
  columns: ColumnDefinition[];
  isDefault?: boolean;
  isTemplate?: boolean;
  isPublic?: boolean;
}

export interface UpdateColumnConfigDto {
  name?: string;
  description?: string;
  columns?: ColumnDefinition[];
  isDefault?: boolean;
  isTemplate?: boolean;
  isPublic?: boolean;
}

export interface QueryColumnConfigDto {
  includeTemplates?: boolean;
  onlyTemplates?: boolean;
  onlyPublic?: boolean;
}

export interface CopyColumnConfigDto {
  name: string;
  description?: string;
}

export const columnConfigApi = {
  // Get all configurations
  getAll: (params?: QueryColumnConfigDto): Promise<{ items: ColumnConfig[]; total: number }> =>
    get('/parts/columns/configs', { params }),

  // Get default configuration
  getDefault: (): Promise<ColumnConfig> => get('/parts/columns/configs/default'),

  // Get configuration by ID
  getById: (id: string): Promise<ColumnConfig> => get(`/parts/columns/configs/${id}`),

  // Create configuration
  create: (data: CreateColumnConfigDto): Promise<ColumnConfig> =>
    post('/parts/columns/configs', data),

  // Update configuration
  update: (id: string, data: UpdateColumnConfigDto): Promise<ColumnConfig> =>
    put(`/parts/columns/configs/${id}`, data),

  // Delete configuration
  delete: (id: string): Promise<void> => del(`/parts/columns/configs/${id}`),

  // Set as default
  setDefault: (id: string): Promise<ColumnConfig> =>
    post(`/parts/columns/configs/${id}/set-default`),

  // Copy configuration
  copy: (id: string, data: CopyColumnConfigDto): Promise<ColumnConfig> =>
    post(`/parts/columns/configs/${id}/copy`, data),
};

// ===================================
// Custom Field API
// ===================================

export type CustomFieldType =
  | 'TEXT'
  | 'NUMBER'
  | 'DECIMAL'
  | 'DATE'
  | 'DATETIME'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'TEXTAREA'
  | 'URL'
  | 'EMAIL'
  | 'PHONE'
  | 'FILE'
  | 'IMAGE'
  | 'RICH_TEXT'
  | 'JSON';

export type CustomFieldStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';

export interface CustomField {
  id: string;
  fieldKey: string;
  fieldName: string;
  fieldNameEn?: string;
  fieldType: CustomFieldType;
  fieldConfig?: any;
  defaultValue?: string;
  required: boolean;
  searchable: boolean;
  sortable: boolean;
  displayOrder: number;
  group?: string;
  helpText?: string;
  status: CustomFieldStatus;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomFieldDto {
  fieldKey: string;
  fieldName: string;
  fieldNameEn?: string;
  fieldType: CustomFieldType;
  fieldConfig?: any;
  defaultValue?: string;
  required?: boolean;
  searchable?: boolean;
  sortable?: boolean;
  displayOrder?: number;
  group?: string;
  helpText?: string;
}

export interface UpdateCustomFieldDto {
  fieldKey?: string;
  fieldName?: string;
  fieldNameEn?: string;
  fieldType?: CustomFieldType;
  fieldConfig?: any;
  defaultValue?: string;
  required?: boolean;
  searchable?: boolean;
  sortable?: boolean;
  displayOrder?: number;
  group?: string;
  helpText?: string;
  status?: CustomFieldStatus;
}

export interface QueryCustomFieldDto {
  status?: CustomFieldStatus;
  group?: string;
  fieldType?: CustomFieldType;
  searchable?: boolean;
  sortable?: boolean;
}

export interface ReorderFieldsDto {
  fields: Array<{ id: string; displayOrder: number }>;
}

export const customFieldApi = {
  // Get all custom fields
  getAll: (params?: QueryCustomFieldDto): Promise<{ items: CustomField[]; total: number }> =>
    get('/parts/custom-fields', { params }),

  // Get groups
  getGroups: (): Promise<{ groups: string[] }> => get('/parts/custom-fields/groups'),

  // Get field by ID
  getById: (id: string): Promise<CustomField> => get(`/parts/custom-fields/${id}`),

  // Get field by key
  getByKey: (fieldKey: string): Promise<CustomField> =>
    get(`/parts/custom-fields/by-key/${fieldKey}`),

  // Create custom field
  create: (data: CreateCustomFieldDto): Promise<CustomField> =>
    post('/parts/custom-fields', data),

  // Update custom field
  update: (id: string, data: UpdateCustomFieldDto): Promise<CustomField> =>
    put(`/parts/custom-fields/${id}`, data),

  // Delete custom field (soft delete)
  delete: (id: string): Promise<CustomField> => del(`/parts/custom-fields/${id}`),

  // Hard delete custom field
  hardDelete: (id: string): Promise<void> => del(`/parts/custom-fields/${id}/hard`),

  // Reorder fields
  reorder: (data: ReorderFieldsDto): Promise<{ success: boolean }> =>
    post('/parts/custom-fields/reorder', data),

  // Activate field
  activate: (id: string): Promise<CustomField> =>
    post(`/parts/custom-fields/${id}/activate`),

  // Deactivate field
  deactivate: (id: string): Promise<CustomField> =>
    post(`/parts/custom-fields/${id}/deactivate`),
};

// 重新导出子模块
export * from './inventory';
export * from './warehouse';
export * from './station';
